import React, { useContext, useEffect, useState } from "react";

import {
  Flex,
  Container,
  Text,
  Box,
  TransparentButton,
  Divider,
  Link,
  SecondaryButton,
  PrimaryButton,
} from "flicket-ui";
import { useRouter } from "next/router";
import useSWR from "swr";
import {
  Layout,
  Footer,
  BottomBar,
  Menu,
  Seo,
  Icon,
  Tabs,
  Status,
  ContentContainer,
} from "~components";
import { BaseContext, UserContext } from "~context";
import { getError, sdk, showToast } from "~lib";
import {
  Information,
  NonLoggedInRenewalBanner,
  RenewalBanner,
  Schedule,
} from "~components/memberships";
import { MembershipKind, MembershipQuery } from "~graphql/sdk";
import Image from "~components/common/Image";

const getMembership = async (_: string, orgId: string, membershipId: string) =>
  sdk({ orgId }).membership({ id: membershipId });

const swrConfig = {
  shouldRetryOnError: false,
};

type EventVenue = MembershipQuery["membership"]["events"][number]["venue"];

const hasMembershipToRenewFetcher = async (_: string, orgId: string) =>
  sdk({ orgId })
    .hasMembershipToRenew()
    .then((res) => res.myMembershipTickets);

const MembershipId = () => {
  const router = useRouter();
  const { organization, isSinglePage } = useContext(BaseContext);
  const { isLoggedIn } = useContext(UserContext);
  const [eventVenues, setEventVenues] = useState<EventVenue[]>([]);
  const [locations, setLocations] = useState("");
  const { data, error } = useSWR<MembershipQuery, Error>(
    organization?.id && router?.query?.membershipId
      ? ["memberships", organization?.id, router?.query?.membershipId]
      : null,
    getMembership,
    swrConfig
  );

  const isLoading = !data && !error;
  const isInactive = data?.membership?.isActive === false;

  useEffect(() => {
    if (isInactive) {
      void router.push("/memberships");
    }
  }, [isInactive]);

  const { data: myMemberships } = useSWR(
    organization?.id && organization?.showMembershipRenewal
      ? ["hasMembershipToRenew", organization?.id]
      : null,
    hasMembershipToRenewFetcher
  );

  const currentMembershipRenewalInformation = myMemberships?.find(
    (membership) =>
      membership.membershipType?.membership.id === router.query.membershipId
  );

  const hasMembershipToRenew =
    !currentMembershipRenewalInformation?.hasRenewedMembership &&
    !!currentMembershipRenewalInformation?.membershipType?.membership
      .renewalMembership?.id;

  if (error) {
    showToast(getError(error, "graphQL"), "error");
  }

  const isDigitalMembership =
    data?.membership?.membershipKind === MembershipKind.Digital;

  const showBanner =
    organization?.showMembershipRenewal && isLoggedIn && hasMembershipToRenew;

  const showNonLoggedInBanner =
    organization?.showMembershipRenewal && !isLoggedIn;

  const membership = data?.membership;

  const cartRecovery = router.query.cartRecovery;

  const membershipReservationLink = cartRecovery
    ? `/memberships/${membership?.id}/reservation?cartRecovery=true`
    : `/memberships/${membership?.id}/reservation`;

  useEffect(() => {
    membership?.events.forEach((membershipEvent) => {
      const eventVenueList = eventVenues;
      eventVenueList.push(membershipEvent.venue);

      const newlist = Array.from(
        new Set(eventVenueList.map((venue) => venue.name))
      ).map((name) => {
        return {
          name: name,
          address: eventVenueList.find((venue) => venue.name === name).address,
        };
      });
      setEventVenues(newlist);
      setLocations(
        newlist
          .map((venue) => `${venue.name}, ${venue?.address?.city}`)
          .join(", ")
      );
    });
  }, [membership]);

  const tabs = membership?.showSchedule
    ? [
        {
          id: "information",
          title: "Information",
          content: <Information {...membership} />,
        },
        {
          id: "schedule",
          title: "Schedule",
          content: <Schedule {...membership} />,
        },
      ]
    : [
        {
          id: "information",
          title: "Information",
          content: <Information {...membership} />,
        },
      ];
  return (
    <>
      <Seo title="Membership detail" description="Membership detail page for" />
      <Menu />
      <Status error={error} loading={isLoading || isInactive}>
        <Flex flexDirection="column" flex={1}>
          <Box d={{ _: "block", md: "none" }}>
            <Flex px={2} py={3}>
              <TransparentButton
                mr={2}
                to={isSinglePage ? "/" : "/memberships"}
              >
                <Icon icon="chevron-left" fontSize={6} color="N800" />
              </TransparentButton>
              <Text
                lineHeight="normal"
                fontSize={6}
                fontWeight="extraBold"
                color="N800"
              >
                {membership?.name}
              </Text>
            </Flex>
            <Divider />
          </Box>
          <Flex
            flexDirection="column"
            flex={1}
            maxH={{ _: "calc(100vh - 73px)", md: "unset" }}
            overflowY={{ _: "auto", md: "visible" }}
          >
            <Layout layoutType="overlay">
              <ContentContainer
                variant="content"
                image={membership?.backgroundImage}
              >
                <Flex flexDir="column" pb={{ _: 2, md: 0 }} flex="1">
                  <Box
                    width="100%"
                    maxWidth="1248px"
                    m={"0 auto" as any}
                    px={{ _: 0, md: 4 }}
                    d={{ _: "none", md: "block" }}
                  >
                    {isSinglePage ? (
                      <Link to="/">
                        <Flex alignItems="center" opacity="inherit">
                          <Icon
                            icon="chevron-left"
                            fontSize={5}
                            color="white"
                          />
                          <Text
                            color="white"
                            fontWeight="extraBold"
                            fontSize={3}
                            lineHeight="medium"
                            ml="3/4"
                          >
                            Back to tickets
                          </Text>
                        </Flex>
                      </Link>
                    ) : (
                      <Link to="/memberships">
                        <Flex alignItems="center" opacity="inherit">
                          <Icon
                            icon="chevron-left"
                            fontSize={5}
                            color="white"
                          />

                          <Text
                            color="white"
                            fontWeight="extraBold"
                            fontSize={3}
                            lineHeight="medium"
                            ml="3/4"
                          >
                            Back to Memberships
                          </Text>
                        </Flex>
                      </Link>
                    )}

                    <Text
                      as={"h1"}
                      color="white"
                      fontWeight="heavy"
                      fontSize={9}
                      lineHeight={"56px" as any}
                      mt={1}
                    >
                      {membership?.name}
                    </Text>

                    <Text
                      as={"h2"}
                      color="white"
                      fontWeight="heavy"
                      fontSize={5}
                      lineHeight={"95%" as any}
                      mb={1}
                    >
                      {locations}
                    </Text>
                  </Box>

                  <Container
                    mb={{ _: 0, md: 10 }}
                    innerProps={{
                      minHeight: { _: 0, md: "320px" },
                      pb: { _: 6, md: 4 },
                      pt: membership?.banner ? { md: 0 } : { md: 4 },
                      overflow: "hidden",
                    }}
                  >
                    {isDigitalMembership && (
                      <Flex
                        p={2}
                        w="100%"
                        alignItems="center"
                        borderRadius="sm"
                        bg="N100"
                        border="1px"
                        borderColor="N400"
                        my={3}
                      >
                        <Icon fontSize={6} icon="info" color="warning" mr={1} />
                        <Text fontSize={3} lineHeight="high" color="N800">
                          Please note that this is a digital membership which
                          provides you online access only
                        </Text>
                      </Flex>
                    )}
                    {membership?.banner && (
                      <Image
                        image={membership?.banner}
                        alt={`${membership.name} banner`}
                        priority={true}
                        // @ts-expect-error override
                        m={{ _: 0, md: "0 -32px 16px -32px" }}
                      />
                    )}

                    {showBanner && <RenewalBanner button="secondary" mb={2} />}
                    {showNonLoggedInBanner && (
                      <NonLoggedInRenewalBanner mb={2} />
                    )}

                    <Status loading={!error && !membership} error={error}>
                      {membership?.types.length > 0 && (
                        <Flex
                          flexDir={{ _: "column", md: "row" }}
                          justifyContent="flex-end"
                          mb={{ _: 2, md: "-18px" as any }}
                        >
                          <PrimaryButton
                            minH="48px"
                            display="flex"
                            w={{ _: "100%", md: "auto" }}
                            ml={{ _: 0, md: 2 }}
                            mt={2}
                            to={membershipReservationLink}
                          >
                            {isSinglePage
                              ? "Get Tickets"
                              : organization.id ===
                                "2648ddb4-b50c-489f-a3eb-ae0249e3db3c"
                              ? "Join now"
                              : "Get Membership"}
                          </PrimaryButton>
                        </Flex>
                      )}
                      <Box mx={{ _: 0, md: "-32px" as any }}>
                        {membership && <Tabs items={tabs} />}
                      </Box>
                      <Divider display={{ _: "none", md: "flex" }} mb={3} />
                      <Flex
                        flexDir={{ _: "column", md: "row" }}
                        justifyContent="flex-end"
                      >
                        {membership?.termsAndConditions && (
                          <SecondaryButton
                            to={`/memberships/${membership?.id}/terms-and-conditions`}
                            minH="48px"
                            w={{ _: "100%", md: "auto" }}
                            mb={{ _: 3, md: 0 }}
                          >
                            Full Terms &amp; Conditions
                          </SecondaryButton>
                        )}
                        <Divider mb={3} display={{ _: "flex", md: "none" }} />

                        {membership?.types.length > 0 && (
                          <PrimaryButton
                            minH="48px"
                            display="flex"
                            w={{ _: "100%", md: "auto" }}
                            ml={{ _: 0, md: 2 }}
                            to={membershipReservationLink}
                          >
                            {isSinglePage
                              ? "Get Tickets"
                              : organization.id ===
                                "2648ddb4-b50c-489f-a3eb-ae0249e3db3c"
                              ? "Join now"
                              : "Get Membership"}
                          </PrimaryButton>
                        )}
                      </Flex>
                    </Status>
                  </Container>
                </Flex>
                <Footer />
              </ContentContainer>
            </Layout>
          </Flex>
        </Flex>
      </Status>
      <BottomBar />
    </>
  );
};

export default MembershipId;
